import { EditableProTable } from '@ant-design/pro-components';
import FormSection from 'components/form-section'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Button, Col, Form, Popconfirm, Row, Select, Typography, Input, Modal } from 'antd';
import { generateUuid } from 'utils/dataUtil';
import Papa from 'papaparse'; // Import papaparse for CSV parsing
import { Upload } from 'antd'; // Import Upload component from antd
import { useTable, useBlockLayout, useRowSelect } from 'react-table';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { TableOutlined } from '@ant-design/icons';

import classes from './Table.module.scss'
import { isNumeric } from 'utils/string';
import { isDecreasing, isIncreasing } from 'utils/number';
import { COLORS } from 'app/codes';
import Chart from 'chart.js/auto';
import TableEditor from './TableEditor';
import { useQuery } from '@tanstack/react-query'
import { getReferenceList, getReferenceData } from 'service/completionOptimizer'

const units = {
  PRESSURE: {
    FIELD: '(psia)',
    METRIC: '(bar)'
  },
  FVF: {
    FIELD: '(ft3/Mscf)',
    METRIC: '(rm3/sm3)'
  },
  VISCOSITY: {
    FIELD: '(cP)',
    METRIC: '(cP)'
  },
  RV: {
    FIELD: '(STB/MSCF)',
    METRIC: '(sm3/sm3)'
  }
}

const GasTable = ({ fields = [], operation }) => {
  const chart = useRef()
  const chartCanvas = useRef()
  const form = Form.useFormInstance()
  const simulationUnit = Form.useWatch(["GENERAL", "unit"])
  const fluidType = Form.useWatch(["GENERAL", "fluid"], form)
  const [data, setData] = useState([]);
  const [editingData, setEditingData] = useState([]);
  const [errors, setErrors] = useState([])
  const [selectedCells, setSelectedCells] = useState([]);
  const [copiedData, setCopiedData] = useState(null);
  const [selectionStart, setSelectionStart] = useState(null);
  const [selectionEnd, setSelectionEnd] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isDragFilling, setIsDragFilling] = useState(false);
  const [dragStartCell, setDragStartCell] = useState(null);
  const [dragEndCell, setDragEndCell] = useState(null);
  const [isTableEditorOpen, setIsTableEditorOpen] = useState(false);
  const tableEditorRef = useRef(null);
  const fetchData = () => {
    const newData = fields.map((field, index) => {
      const fieldValue = form.getFieldValue(["PVT", "gas", field.name]);
      if (!fieldValue) return null;
      return {
        ...fieldValue,
        arrayIndex: index
      };
    }).filter(Boolean);
    setData(newData);
    console.log('newData', newData)
    setEditingData(newData);
  }

  useEffect(() => {
    validateData(data)
  }, [data])

  useEffect(() => {
    fetchData()
  }, [fields])

  const columns = useMemo(
    () => {
      const baseColumns = [
        {
          key: 'key',
          dataIndex: 'key',
          title: '#',
          editable: false,
          render: (dom, record, index) => index + 1
        },
        {
          key: 'PRESSURE',
          dataIndex: 'PRESSURE',
          title: `Pressure ${units.PRESSURE[simulationUnit] ?? ''}`,
          editable: true
        }
      ];

      // Add Rv column right after Pressure when fluid type is CONDENSATE
      if (fluidType === 'CONDENSATE') {
        baseColumns.push({
          key: 'RV',
          dataIndex: 'RV',
          title: `Rv ${units.RV[simulationUnit] ?? ''}`,
          editable: true,
        });
      }

      // Add remaining columns
      baseColumns.push(
        {
          key: 'FVF',
          dataIndex: 'FVF',
          title: `FVF ${units.FVF[simulationUnit] ?? ''}`,
          editable: true,
        },
        {
          key: 'VISCOSITY',
          dataIndex: 'VISCOSITY',
          title: `Viscosity ${units.VISCOSITY[simulationUnit] ?? ''}`,
          editable: true,
        },
        {
          title: "Operation",
          valueType: "option",
          key: "operation",
          width: 160,
          render: (text, record, index, action) => [
            <Button
              key="editable"
              type="text"
              className="text-primary"
              onClick={() => {
                action?.startEditable?.(record.key);
              }}
            >
              Edit
            </Button>,
            <Popconfirm
              key="delete"
              title="Sure to delete?"
              onConfirm={() => {
                // Remove from Form.List to keep UI in sync
                operation?.remove?.(index);
                console.log('index', index)
                const currentGasData = form.getFieldValue(["PVT", "gas"]);
                console.log('currentGasData', currentGasData)
                const newGasData = currentGasData.filter((_, i) => i !== index);
                console.log('newGasData', newGasData)                
                // Set the entire gas array back to form
                form.setFieldValue(["PVT", "gas"], newGasData);
                
                
                // Update local state
                fetchData();
              }}
            >
              <Button type="text" danger>Delete</Button>
            </Popconfirm>
          ],
        }
      );

      return baseColumns;
    }
  , [operation, simulationUnit, fluidType]);

  const handleFileUpload = (file) => {
    Papa.parse(file, {
      header: true,
      dynamicTyping: true,
      complete: (result) => {
        const parsedData = result.data
          .filter(row => Object.values(row).some(value => value !== null))
          .map((row, index) => ({
            ...row,
            key: generateUuid(),
          }));

        // Update the data state
        setData(parsedData);

        // Auto-save the data to form fields
        fields.forEach((field, index) => {
          if (parsedData[index]) {
            form.setFieldValue(["PVT", "gas", field.name], parsedData[index]);
          }
        });

        validateData(parsedData);
      },
    });
    return false;
  };

  const chartOptions = useMemo(() => {
      const labels = data.map(row => row[columns[1].dataIndex])
      return {
        labels,
        datasets: [
          {
            label: columns[2].title,
            data: data.map(row => row[columns[2].dataIndex]),
            fill: false,
            borderColor: COLORS[0],
            yAxisID: columns[2].dataIndex,
            tension: 0.4
          },
          {
            label: columns[3].title,
            data: data.map(row => row[columns[3].dataIndex]),
            fill: false,
            borderColor: COLORS[1],
            yAxisID: columns[3].dataIndex,
            tension: 0.4
          }
        ]
      }
    },
    [columns, data])

  const updateChart = (config) => {
    if (!chartCanvas.current) return
    if (!chart.current) return

    chart.current.data.labels = config.labels
    chart.current.data.datasets = config.datasets

    chart.current.update()
  }

  useEffect(() => {
    if (chart.current) chart.current.destroy()
    chart.current = new Chart(chartCanvas.current, {
      type: 'line',
      data: {
        labels: [],
        datasets: []
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          [columns[2].dataIndex]: {
            type: 'linear',
            display: true,
            position: 'left',
            title: {
              display: true,
              text: columns[2].title
            }
          },
          [columns[3].dataIndex]: {
            type: 'linear',
            display: true,
            position: 'right',
            title: {
              display: true,
              text: columns[3].title
            }
          },
          x: {
            display: true,
            title: {
              display: true,
              text: columns[1].title
            }
          }
        }
      }
    })
  }, [])

  useEffect(() => {
    updateChart(chartOptions)
  }, [chartOptions])

  const validateData = useCallback((_data = []) => {
    const _errors = [];
    
    // Validation rules specific to fluid type
    if (fluidType === 'GAS') {
      // For gas fluid type, require at least 2 records
      if (_data.length < 2) {
        _errors.push('Gas fluid type requires at least 2 records');
      }
    } else if (fluidType === 'BLACK_OIL' || fluidType === 'CONDENSATE') {
      // Different rules for other fluid types
    }

    if (!_data.length) return _errors;

    const pressureValues = [];
    const fvfValues = [];
    const viscosityValues = [];

    _data.forEach((row, index) => {
      if (!isNumeric(row.PRESSURE)) {
        _errors.push(`Value of Pressure in row #${index + 1} must be a number`);
      } else {
        const pressure = parseFloat(row.PRESSURE);
        if (pressure <= 0) {
          _errors.push(`Value of Pressure in row #${index + 1} must be > 0`);
        } else pressureValues.push(pressure);
      }
      if (!isNumeric(row.FVF)) {
        _errors.push(`Value of FVF in row #${index + 1} must be a number`);
      } else {
        const fvf = parseFloat(row.FVF);
        if (fvf <= 0) {
          _errors.push(`Value of FVF in row #${index + 1} must be > 0`);
        } else fvfValues.push(fvf);
      }
      if (!isNumeric(row.VISCOSITY)) {
        _errors.push(`Value of Viscosity in row #${index + 1} must be a number`);
      } else {
        const viscosity = parseFloat(row.VISCOSITY);
        if (viscosity <= 0) {
          _errors.push(`Value of Viscosity in row #${index + 1} must be > 0`);
        } else viscosityValues.push(viscosity);
      }
      if (fluidType === 'CONDENSATE') {
        if (!isNumeric(row.RV)) {
          _errors.push(`Value of Rv in row #${index + 1} must be a number`);
        } else {
          const rv = parseFloat(row.RV);
          if (rv < 0) {
            _errors.push(`Value of Rv in row #${index + 1} must be >= 0`);
          }
        }
      }
    });

    if (_errors.length) {
      return _errors;
    }

    const isPressureIncreasing = isIncreasing(pressureValues);
    if (!isPressureIncreasing) {
      _errors.push(`The values of Pressure must be increasing`);
    }

    const isFVFDecreasing = isDecreasing(fvfValues);
    if (!isFVFDecreasing) {
      _errors.push(`The values of FVF must be decreasing`);
    }

    const isViscosityIncreasing = isIncreasing(viscosityValues);
    if (!isViscosityIncreasing) {
      _errors.push(`The values of Viscosity must be increasing`);
    }

    return _errors;
  }, [fluidType]);

  // Mouse handlers for selection
  const handleMouseDown = (e, rowIndex, colIndex) => {
    // Only handle left mouse button
    if (e.button !== 0) return;
    setIsDragging(true);
    setSelectionStart({ rowIndex, colIndex });
    setSelectionEnd({ rowIndex, colIndex });
  };

  const handleMouseEnter = (rowIndex, colIndex) => {
    if (isDragging) {
      setSelectionEnd({ rowIndex, colIndex });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    document.addEventListener('mouseup', handleMouseUp);
    return () => document.removeEventListener('mouseup', handleMouseUp);
  }, []);

  // Check if a cell is within the current selection
  const isCellSelected = (rowIndex, colIndex) => {
    if (!selectionStart || !selectionEnd) return false;
    
    const startRow = Math.min(selectionStart.rowIndex, selectionEnd.rowIndex);
    const endRow = Math.max(selectionStart.rowIndex, selectionEnd.rowIndex);
    const startCol = Math.min(selectionStart.colIndex, selectionEnd.colIndex);
    const endCol = Math.max(selectionStart.colIndex, selectionEnd.colIndex);

    return rowIndex >= startRow && rowIndex <= endRow && 
           colIndex >= startCol && colIndex <= endCol;
  };

  // Create a shared copy function
  const copySelectedCells = useCallback(() => {
    if (!selectionStart || !selectionEnd) {
      return;
    }
    
    const startRow = Math.min(selectionStart.rowIndex, selectionEnd.rowIndex);
    const endRow = Math.max(selectionStart.rowIndex, selectionEnd.rowIndex);
    const startCol = Math.min(selectionStart.colIndex, selectionEnd.colIndex);
    const endCol = Math.max(selectionStart.colIndex, selectionEnd.colIndex);


    // Get the relevant columns
    const selectedColumns = columns
      .filter((col, index) => index >= startCol && index <= endCol && col.dataIndex !== 'key' && col.key !== 'operation');


    // Format data for clipboard
    const selectedRows = data.slice(startRow, endRow + 1);

    const formattedData = selectedRows.map(row => {
      return selectedColumns
        .map(col => {
          const value = row[col.dataIndex];
          return value != null ? String(value) : '';
        })
        .join('\t');
    }).join('\n');


    // Use a temporary textarea for copying
    const textarea = document.createElement('textarea');
    textarea.value = formattedData;
    document.body.appendChild(textarea);
    textarea.select();
    
    try {
      document.execCommand('copy');
    } catch (err) {
      // Fallback to clipboard API
      navigator.clipboard.writeText(formattedData)
        .then(() => console.log('Data copied successfully using clipboard API'))
        .catch(err => console.error('Failed to copy:', err));
    } finally {
      document.body.removeChild(textarea);
    }
  }, [selectionStart, selectionEnd, data, columns]);

  // Update the Ctrl+C handler to use the shared function
  useEffect(() => {
    const handleKeyboardCopy = (e) => {
      if (!e.ctrlKey || e.key !== 'c') return;
      copySelectedCells();
    };

    document.addEventListener('keydown', handleKeyboardCopy);
    return () => document.removeEventListener('keydown', handleKeyboardCopy);
  }, [copySelectedCells]);

  // Update the paste handler
  useEffect(() => {
    const handlePaste = async (e) => {
      if (!selectionStart) return;
      e.preventDefault();

      try {
        // Get clipboard data
        let clipboardData = e.clipboardData.getData('text');
        clipboardData = clipboardData.replace(/\r\n/g, '\n').trim();
        const pastedRows = clipboardData.split('\n').map(row => 
          row.split('\t').map(cell => cell.trim())
        );

        // Create new data array
        let newData = [...data];
        const startRow = selectionStart.rowIndex;

        // Split the data into before and after selection
        const beforeRows = newData.slice(0, startRow);
        const afterRows = newData.slice(startRow + 1);

        // Create rows for pasted data
        const pastedData = pastedRows.map(row => ({
          key: generateUuid(),
          PRESSURE: '',
          FVF: '',
          VISCOSITY: ''
        }));

        // Combine all rows: before + pasted + after
        newData = [...beforeRows, ...pastedData, ...afterRows];

        // Fill in the pasted values
        pastedRows.forEach((row, rowIndex) => {
          const targetRow = startRow + rowIndex;
          
          row.forEach((cell, colIndex) => {
            const targetCol = selectionStart.colIndex + colIndex;
            const columnKey = columns[targetCol]?.dataIndex;
            
            if (columnKey && columnKey !== 'key' && columnKey !== 'operation') {
              newData[targetRow][columnKey] = isNumeric(cell) ? parseFloat(cell) : cell;
            }
          });
        });

        // Update data and form
        setData(newData);
        fields.forEach((field, index) => {
          if (newData[index]) {
            form.setFieldValue(["PVT", "gas", field.name], newData[index]);
          }
        });

      } catch (error) {
        console.error('Paste error:', error);
      }
    };

    document.addEventListener('paste', handlePaste);
    return () => document.removeEventListener('paste', handlePaste);
  }, [selectionStart, data, columns, fields, form]);

  // Enhance columns with selection behavior
  const enhancedColumns = useMemo(() => {
    return columns.map((col, colIndex) => ({
      ...col,
      render: (text, record, rowIndex, action) => {
        if (col.key === 'key') return rowIndex + 1;
        if (col.key === 'operation') return col.render(text, record, rowIndex, action);
        
        return (
          <div
            className={`${classes.tableCellWrapper} ${isCellSelected(rowIndex, colIndex) ? classes.selected : ''}`}
            onMouseDown={(e) => handleMouseDown(e, rowIndex, colIndex)}
            onMouseEnter={() => handleMouseEnter(rowIndex, colIndex)}
          >
            {/* Use EditableProTable's built-in cell rendering */}
            {record.editable ? (
              <Form.Item name={[record.key, col.dataIndex]}>
                <Input />
              </Form.Item>
            ) : (
              text
            )}
          </div>
        );
      }
    }));
  }, [columns, isCellSelected, handleMouseDown, handleMouseEnter]);

  // Update the editable config
  const editableConfig = {
    type: 'multiple',
    actionRender: (row, config, defaultDom) => [
      defaultDom.save,
      defaultDom.cancel,
    ],
    onSave: (rowKey, data, row) => {
      const { index, isNew, ...recordData } = data
      if (isNew) {
        // Generate new key with underscores instead of hyphens
        const newKey = generateUuid().replace(/-/g, '_')
        operation?.add?.({
          ...recordData,
          key: newKey
        })
      } else {
        form.setFieldValue(["PVT", "gas", index], recordData)
        fetchData()
      }
    }
  };

  const handleTableEditorOpen = () => {
    setEditingData(data); // Reset editing data to original data when opening editor
    setIsTableEditorOpen(true);
  };

  const handleTableEditorCancel = () => {
    setEditingData(data); // Reset editing data to original data
    setIsTableEditorOpen(false);
  };

  const handleTableEditorSave = () => {
    const currentEditingData = tableEditorRef.current?.getCurrentData() || [];
    
    // Remove existing fields
    const fieldCount = fields.length;
    for (let i = fieldCount - 1; i >= 0; i--) {
      operation?.remove?.(i);
    }

    // Add all rows from editing data
    currentEditingData.forEach((row) => {
      const newRow = {
        PRESSURE: row.PRESSURE,
        FVF: row.FVF,
        VISCOSITY: row.VISCOSITY,
        ...(fluidType === 'CONDENSATE' && { RV: row.RV })
      };
      operation?.add?.(newRow);
    });

    setData(currentEditingData);
    setIsTableEditorOpen(false);
    fetchData();
  };

  // Add query to fetch gas references
  const { data: references } = useQuery({
    queryKey: ['references', 'pvt', 'gas'],
    queryFn: () => {
      // Log the reference list request
      console.log('Fetching PVT Gas references list...')
      return getReferenceList('pvt', 'gas').then(data => {
        return data
      })
    }
  })

  // Convert references to Select options
  const referenceOptions = useMemo(() => {
    if (!references) return []
    return references.map(ref => ({
      label: ref.name,
      value: ref.id
    }))
  }, [references])

  // Update reference change handler
  const handleReferenceChange = async (value) => {
    if (!value) return

    try {
      // Get specific reference data using new API
      const referenceData = await getReferenceData('pvt', 'gas', value)

      // Remove existing fields
      const fieldCount = fields.length
      for (let i = fieldCount - 1; i >= 0; i--) {
        operation?.remove?.(i)
      }

      // Convert object to array and filter out length property
      const dataArray = Object.entries(referenceData)
        .filter(([key]) => key !== 'length')
        .map(([_, value]) => value)

      // Add reference data rows
      dataArray.forEach(row => {
        const newRow = {
          PRESSURE: row.pressure,
          FVF: row.fvf,
          VISCOSITY: row.viscosity,
          ...(fluidType === 'CONDENSATE' && { RV: row.rv })
        }
        operation?.add(newRow)
      })
    } catch (error) {
      console.error('Error loading reference data:', error)
    }
  }

  return (
    <FormSection
      title={`Gas ${fluidType === 'GAS' ? '(Primary Fluid)' : ''}`}
      extra={
        <div className="d-flex justify-content-end align-items-center gap-2">
          <Button 
            icon={<TableOutlined />}
            onClick={handleTableEditorOpen}
            title="Open Table Editor"
          />
          <Upload
            accept=".csv"
            showUploadList={false}
            beforeUpload={handleFileUpload}
          >
            <Button type="primary">Upload CSV</Button>
          </Upload>
          <Button onClick={copySelectedCells}>Copy</Button>
          <Typography.Text className="text-light">Reference</Typography.Text>
          <Form.Item 
            name={['PVT', 'gas', 'reference']}
            noStyle
          >
            <Select
              style={{ minWidth: 120 }} 
              dropdownMatchSelectWidth={false}
              options={referenceOptions}
              onChange={handleReferenceChange}
              allowClear
            />
          </Form.Item>
        </div>
      }
    >
      <Row>
        <Col span={12}>
          <EditableProTable
            options={false}
            pagination={false}
            search={false}
            columns={enhancedColumns}
            value={data}
            rowKey="key"
            sticky={{
              offsetHeader: 32
            }}
            className={classes.tableContainer}
            cardProps={{
              className: classes.card
            }}
            tableClassName={classes.table}
            tableExtraRender={() => (
              <ul className="px-24 text-danger">
                {errors.map(errMsg => (
                  <li key={errMsg}>
                    <Typography.Text type="danger">{errMsg}</Typography.Text>
                  </li>
                ))}
              </ul>
            )}
            editable={editableConfig}
            recordCreatorProps={{
              record: {
                key: generateUuid().replace(/-/g, '_'),
                isNew: true
              }
            }}
          />
        </Col>
        <Col span={12} className="pb-2">
          <canvas ref={chartCanvas} />
        </Col>
      </Row>
      <Modal
        title="Table Editor"
        open={isTableEditorOpen}
        onCancel={handleTableEditorCancel}
        width={800}
        style={{ top: 20 }}
        bodyStyle={{ 
          padding: '12px',
          height: 'calc(80vh - 108px)',
          overflow: 'hidden'
        }}
        footer={[
          <Button key="cancel" onClick={handleTableEditorCancel}>
            Cancel
          </Button>,
          <Button 
            key="save" 
            type="primary" 
            onClick={handleTableEditorSave}
          >
            Save
          </Button>
        ]}
      >
        <div style={{ height: '100%' }}>
          <TableEditor
            ref={tableEditorRef}
            data={editingData}
            columns={columns}
            onDataChange={setEditingData}
          />
        </div>
      </Modal>
    </FormSection>
  );
};

export default GasTable

